/**
 * A dependency-free RFC 4180 CSV parser (M6 import tooling). Field- and record-correct:
 * handles quoted fields, embedded commas/newlines/quotes (""), CRLF or LF line endings, and a
 * trailing newline. Pure + isomorphic so the web preview and the API commit parse identically
 * (one code path, no drift). The ICP's data lives in Jobber/Housecall/QBO CSV exports; getting
 * it in cleanly is the difference between a self-serve trial and a dead trial.
 */

/** Parse CSV text into rows of string cells. Empty input → []. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  let i = 0;
  // Strip a leading UTF-8 BOM (Excel/Windows exports love to add one).
  if (text.charCodeAt(0) === 0xfeff) i = 1;

  const endField = () => {
    row.push(field);
    field = '';
  };
  const endRow = () => {
    endField();
    rows.push(row);
    row = [];
  };

  for (; i < text.length; i++) {
    const c = text[i]!;
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"'; // escaped quote
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      endField();
    } else if (c === '\r') {
      if (text[i + 1] === '\n') i++;
      endRow();
    } else if (c === '\n') {
      endRow();
    } else {
      field += c;
    }
  }
  // Flush the final field/row unless the text ended exactly on a newline.
  if (field !== '' || row.length > 0) endRow();
  return rows;
}

export interface CsvTable {
  headers: string[];
  rows: Record<string, string>[];
}

/**
 * Parse CSV into a header-keyed table. The first non-empty row is the header; blank trailing
 * rows are dropped. Duplicate headers are disambiguated (`name`, `name_2`) so no column is lost.
 */
export function parseCsvTable(text: string): CsvTable {
  const raw = parseCsv(text).filter((r) => r.some((c) => c.trim() !== ''));
  if (raw.length === 0) return { headers: [], rows: [] };
  const seen = new Map<string, number>();
  const headers = raw[0]!.map((h) => {
    const key = h.trim();
    const n = (seen.get(key) ?? 0) + 1;
    seen.set(key, n);
    return n === 1 ? key : `${key}_${n}`;
  });
  const rows = raw.slice(1).map((cells) => {
    const obj: Record<string, string> = {};
    headers.forEach((h, idx) => {
      obj[h] = (cells[idx] ?? '').trim();
    });
    return obj;
  });
  return { headers, rows };
}
